import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocalStorage } from '@uidotdev/usehooks';
import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as DeadRobot } from '../../assets/icons/DeadRobot.svg?component';
import { useScrollState } from '../../hooks/useScrollState';
import { useVisibilityState } from '../../hooks/useVisibilityState';
import SystemData from '../../lib/systemData';
import { useAiStore } from '../../stores/AiStore';
import { Badge } from '../Badge';
import { Button } from '../Button/Button';
import { SmartsMarkdown } from './SmartsMarkdown';
import { aiApiCall } from './utils';
import { stores } from '../../stores';
import { IconButton } from '../Button/IconButton';
import { mdiThumbDown, mdiThumbDownOutline, mdiThumbUp, mdiThumbUpOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Toast } from '../Toast/Toast';
import { queryFn, queryKey, SmartHelpQueryResult } from './SmartHelpQuery';
import AILogoStarPurple from '../../assets/ai/logo.ai.star.purple.svg';
import AIIconHelp from '../../assets/ai/icon.ai.help.svg';
import {
	arrow,
	autoUpdate,
	flip,
	FloatingArrow,
	FloatingPortal,
	offset,
	Placement,
	safePolygon,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useHover,
	useInteractions,
	useRole,
} from '@floating-ui/react';
import { SmartsSubmitButton } from './SmartsContent';
import { declareVariants } from '../../lib/variantMatch';
import { cn } from '../../lib/cn';

export type SmartHelpContentProps = {
	query: string;
	question: string;
	sources?: ('relevant' | 'prebid')[];
	answer?: string;
	onClose?: () => void;
	showVote?: boolean;
	showFollowUp?: boolean;
	showFollowUpSuggestion?: boolean;
};

export type SmartHelpProps = SmartHelpContentProps & {
	variant?: 'full' | 'full-no-icon' | 'compact';
	side?: 'left' | 'right';
	visible?: boolean;
	label?: string;
	showFollowUp?: boolean;
	showFollowUpSuggestion?: boolean;
	showVote?: boolean;
};

export function SmartHelpContent({
	query,
	question,
	sources,
	answer: initialAnswer,
	onClose,
	showVote = true,
	showFollowUp = false,
	showFollowUpSuggestion = false,
}: SmartHelpContentProps) {
	const [answer, setAnswer] = useState(initialAnswer);
	const [followUpSuggestion, setFollowUpSuggestion] = useState<string | undefined>(undefined);
	const answerRef = useRef<HTMLDivElement>(null);
	const { setIsOpen: aiSetIsOpen, sendMessage: aiSendMessage } = useAiStore();
	const [language] = useLocalStorage<string>(`aiLang-${stores.identity.userId()}`, 'eng');
	const inputRef = useRef<HTMLInputElement>(null);
	const followUpEnabled = showFollowUp && (SystemData as any).genericData?.aiEnabled && ((SystemData as any).genericData?.aiAdminOnly === false || stores.identity.isAdministrator());
	const [vote, setVote] = useState<-1 | 0 | 1>(0);

	const helpQuery = useQuery<SmartHelpQueryResult>({
		queryKey: queryKey({ query, question, sources }),
		queryFn: () => queryFn({ query, question, sources }),
		enabled: true,
		refetchOnWindowFocus: false,
		retry: false,
		staleTime: 1000 * 60,
	});

	const voteMutation = useMutation({
		mutationFn: async (value: -1 | 0 | 1) => {
			return await aiApiCall('aiHelp', { query, question, language, sources, vote: value });
		},
		onMutate: (value) => {
			setVote(value);
		},
	});

	useEffect(() => {
		if (helpQuery.data) {
			setAnswer(helpQuery.data.text);
			setFollowUpSuggestion(helpQuery.data.followup);
			setVote(helpQuery.data.vote);
		}
	}, [helpQuery.data]);

	const handleSend = (e: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		e.stopPropagation();
		let message = inputRef.current?.value;
		if (!message && followUpEnabled && followUpSuggestion) {
			message = followUpSuggestion;
		}
		if (message) {
			const helpKey = helpQuery.data?.key;
			aiSetIsOpen(true);
			aiSendMessage(message, { helpKey });
			if (inputRef.current) {
				inputRef.current.value = '';
			}
			onClose?.();
		}
	};

	return (
		<>
			{/* Error overlay */}
			{helpQuery.isError && (
				<div className={cn(
					'absolute text-cherry-500 inset-0 flex flex-col items-center justify-center text-center backdrop-blur-xs bg-white/50 z-1 animate-fade-in',
				)}>
					<DeadRobot className="size-20 text-cherry-500 -mt-16 rotate-12" />
					<p className="text-base text-grey-650 mt-4 px-4 leading-relaxed">
						Sorry, we failed to fetch the latest information.
						<br />
						You may have better luck finding it in our <span className="font-medium">manual</span> instead!
					</p>
					<Button
						variant="outlined"
						href={`https://help.relevant-digital.com/knowledge/kb-search-results?term=${query}`}
						className="mt-6"
					>
						Relevant Yield Manual
					</Button>
				</div>
			)}
			<div className="flex flex-col justify-between">
				<div className="relative">
					{/* Answer */}
					<div
						ref={answerRef}
						className="text-sm text-grey-800 h-[400px] w-full overflow-y-auto pr-1 block"
					>
						<Badge color="ai">AI Help</Badge>
						{/* Skeleton while loading */}
						{!helpQuery.isSuccess && (
							<>
								<h1 className={cn(
									'bg-grey-100 rounded-md text-lg text-transparent font-medium my-2',
									helpQuery.isFetching && 'animate-pulse',
								)}>
									Lorem ipsum
								</h1>
								{/* This has placeholder content cause it's the easiest way to create a multiline box that looks like text with gaps in between lines :) */}
								<span className={cn(
									'inline bg-grey-100 rounded-xs text-transparent my-2',
									helpQuery.isFetching && 'animate-pulse',
								)}>
									Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.
								</span>
							</>
						)}
						<div className={cn(
							'transition-opacity duration-500',
							helpQuery.isLoading ? 'opacity-0' : 'opacity-100',
						)}>
							<SmartsMarkdown>
								{helpQuery.data?.text}
							</SmartsMarkdown>

							{/* "Was this helpful?" */}
							{showVote && (
								<div className="flex items-center">
									<span className="text-base text-grey-800 leading-none">Was this helpful?</span>
									<IconButton size="m" icon={[<Icon path={mdiThumbUpOutline} />, <Icon path={mdiThumbUp} />]} className="ml-2 data-active:not-hover:bg-transparent" active={vote === 1} onClick={(e, active) => voteMutation.mutate(active ? 1 : 0)} />
									<IconButton size="m" icon={[<Icon path={mdiThumbDownOutline} />, <Icon path={mdiThumbDown} />]} className="ml-1 data-active:not-hover:bg-transparent" active={vote === -1} onClick={(e, active) => voteMutation.mutate(active ? -1 : 0)} />
									{voteMutation.isSuccess && vote !== 0 && (
										<Toast status="success" message="Thank you! Your feedback is crucial in helping us understand where our documentation needs more attention! ✨" timeout={5} />
									)}
								</div>
							)}
						</div>
					</div>

					{/* Fade-out gradient when overflowing */}
					<FadingScrollMargins scrollRef={answerRef} />
				</div>

				{/* Input */}
				{followUpEnabled && (
					<div className="pt-3 border-grey-200">
						<div className="relative">
							<form onSubmit={handleSend}>
								<input
									ref={inputRef}
									type="text"
									className="w-full pr-10 text-grey-900 border border-grey-150 rounded-md px-3 py-2 focus:border-black focus:outline-hidden placeholder-grey-400 focus:placeholder-transparent"
									placeholder={helpQuery.isSuccess ? ((showFollowUpSuggestion && followUpSuggestion) || 'Ask Relevant AI') : ''}
									disabled={!helpQuery.isSuccess}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											e.stopPropagation();
											handleSend(e);
										}
									}}
								/>
								<SmartsSubmitButton disabled={!helpQuery.isSuccess} className="absolute right-2 top-1/2 -translate-y-1/2 transform" />
							</form>
						</div>
					</div>
				)}
			</div>
		</>
	);
}

export function SmartHelp({
	query,
	question,
	sources,
	answer: initialAnswer,
	variant = 'full',
	side: initialSide,
	visible: initialVisible,
	label,
	showFollowUp = true,
	showVote = true,
	showFollowUpSuggestion = false,
}: SmartHelpProps) {
	const [visible, setVisible, shouldRender] = useVisibilityState(!!initialVisible, 300);
	const arrowRef = useRef(null);
	const isCompactTrigger = variant === 'compact';

	const variants = declareVariants({
		variant,
	});

	// Determine placement based on side preference
	const placement: Placement = initialSide === 'left' ? 'left-start' : 'right-start';

	const { refs, floatingStyles, context, middlewareData } = useFloating({
		open: visible,
		onOpenChange: setVisible,
		placement,
		middleware: [
			offset({ mainAxis: 16 }),
			shift({ padding: 5 }),
			flip(),
			arrow({ element: arrowRef, padding: 12 }),
		],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);
	const hover = useHover(context, { enabled: isCompactTrigger, handleClose: safePolygon() });

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		hover,
		dismiss,
		role,
	]);

	return (
		<div className="inline-flex relative">
			{shouldRender && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						{...getFloatingProps()}
						className={cn(
							'box-border transition-opacity duration-300 bg-white rounded-lg shadow-lg border border-borders-primary w-[450px] z-[var(--z-aiHelp)]',
							'shadow-[0_4px_7px_2px_rgba(25,25,25,0.05)]',
							// visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
						)}
					>
						<FloatingArrow
							ref={arrowRef}
							context={context}
							width={16}
							height={8}
							strokeWidth={1}
							className="fill-white [&>path:first-of-type]:stroke-borders-primary [&>path:last-of-type]:stroke-white"
						/>
						<div className="relative overflow-clip p-4 rounded-lg">
							<SmartHelpContent
								query={query}
								question={question}
								sources={sources}
								answer={initialAnswer}
								showVote={showVote}
								showFollowUp={showFollowUp}
								showFollowUpSuggestion={showFollowUpSuggestion}
								onClose={() => setVisible(false)}
							/>
						</div>
					</div>
				</FloatingPortal>
			)}

			{(variant === 'full' || variant === 'full-no-icon') && (
				<button
					ref={refs.setReference}
					{...getReferenceProps()}
					className={cn(
						'relative bg-linear-(--gradient-ai-border) rounded-full py-1',
						'hover:shadow-[0_4px_7px_2px_rgba(25,25,25,0.05)] transition-shadow duration-150',
						variants({
							variant: {
								full: 'pl-1.5 pr-2.5',
								'full-no-icon': 'px-2.5',
								compact: undefined,
							},
						}),
						visible && 'shadow-[0_4px_7px_2px_rgba(25,25,25,0.05)]',
					)}
				>
					<div className="absolute bg-white rounded-full inset-[2px]" />
					<div className="relative inset-0 flex items-center justify-center gap-1.5">
						{variant !== 'full-no-icon' && (
							<img src={AILogoStarPurple} alt="Relevant AI" className="size-[16px]" />
						)}
						<span className="text-sm leading-5 font-semibold text-transparent bg-linear-(--gradient-ai-icon-no-text) bg-clip-text">
							{label ?? 'Help'}
						</span>
					</div>
				</button>
			)}

			{isCompactTrigger && (
				<button
					ref={refs.setReference}
					{...getReferenceProps()}
					type="button"
					className={cn(
						'flex shrink-0 items-center justify-center p-0',
						variants({
							variant: {
								full: undefined,
								'full-no-icon': undefined,
								compact: 'size-[24px]',
							},
						}),
					)}
				>
					<img
						src={AIIconHelp}
						alt="AI Help"
						className="size-[20px]"
					/>
				</button>
			)}
		</div>
	);
}

function FadingScrollMargins({ scrollRef }: {
	scrollRef: React.RefObject<HTMLElement>;
}) {
	const { scrollY, scrollNegY } = useScrollState(scrollRef);

	return (
		<>
			<div
				className="absolute top-0 left-0 right-0 h-20 bg-linear-to-b from-white to-transparent pointer-events-none"
				style={{
					opacity: Math.min(1, scrollY / 20),
				}}
			/>
			<div
				className="absolute bottom-0 left-0 right-0 h-20 bg-linear-to-t from-white to-transparent pointer-events-none"
				style={{
					opacity: Math.min(1, scrollNegY / 20),
				}}
			/>
		</>
	);
}
